import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { PatientDistributionFilter, PatientDistributionType, RelationGeography } from '../../../swagger-api';
import { SessionService } from '../../../auth/session.service';
import { PatientLinkRequestService } from '../../user-relation-requests/services/patient-link-request.service';
import { OperationInfoService } from '../../../common/long-operations/operation-info.service';
import { DistributionPreferencesData, DistributionPreferencesService } from './distribution-preferences.service';
import { FormControl, FormGroup } from '@angular/forms';
import { unsubscribeAll } from '../../../common/app.helpers';
import { TranslateService } from '@ngx-translate/core';
import { calculateRelationsCount } from '../../user-relation-requests/services/relation-shared.service';

export interface CountryOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-distribution-preferences',
  templateUrl: './distribution-preferences.component.html',
  styleUrls: ['./distribution-preferences.component.scss'],
})
export class DistributionPreferencesComponent implements OnInit, OnDestroy {
  private readonly subscriptions$: Subscription[] = [];

  private readonly sessionService = inject(SessionService);
  private readonly patientLinkService = inject(PatientLinkRequestService);
  private readonly operationInfoService = inject(OperationInfoService);
  private readonly distributionPreferencesService = inject(DistributionPreferencesService);
  private readonly translateService = inject(TranslateService);

  private readonly defaultValue: string = 'all-countries';

  private userId: string;
  private initialCountry?: string;

  public totalPatientCount: number;

  public form = new FormGroup({ selectedCountry: new FormControl<string>(this.defaultValue) });
  public countryOptions: CountryOption[] = [{ label: 'All Countries', value: this.defaultValue }];

  public isFormChanged = false;
  public distributionText: string;

  private readonly relationGeography$$: Subject<RelationGeography> = new Subject<RelationGeography>();

  public ngOnInit(): void {
    this.userId = this.sessionService.userId;

    this.subscribeToFormChanges();
    this.subscribeOnRelationGeography();
    this.subscribeOnDoctorPreferencesChanges();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public readonly relationGeography$: Observable<RelationGeography> = this.relationGeography$$.asObservable();
  public noRegionalData = false;

  private subscribeOnDoctorPreferencesChanges(): void {
    this.subscriptions$.push(
      this.distributionPreferencesService.preferencesData.subscribe(
        (distributionResult: DistributionPreferencesData) => {
          this.initialCountry = distributionResult.selectedCountry;
          this.setDropdownValue();
          this.setCountries(distributionResult.countriesList);
          this.totalPatientCount = distributionResult.total;
          this.setPreferencesText(distributionResult.relationGeography);

          this.relationGeography$$.next(distributionResult.relationGeography);
        },
      ),
    );

    this.distributionPreferencesService.getDistributionResult(this.userId, true);
  }

  private subscribeToFormChanges(): void {
    this.subscriptions$.push(
      this.form.valueChanges.subscribe(() => {
        this.updateFormChangedState();
        this.loadDistributionData();
      }),
    );
  }

  private subscribeOnRelationGeography(): void {
    this.subscriptions$.push(
      this.patientLinkService.relationGeographyFetched.subscribe((relationGeography: RelationGeography) =>
        this.handleRelationGeographyFetched(relationGeography),
      ),
    );
  }

  private setPreferencesText(relationGeography: RelationGeography): void {
    const isDefaultValue: boolean = this.defaultValue == this.form.value.selectedCountry;
    if (isDefaultValue) {
      this.distributionText = this.translateService.instant('GEOGRAPHIC_DISTRIBUTION_BY_ALL_COUNTRIES', {
        patients_counts: this.totalPatientCount,
      }) as string;
    } else {
      const relationCount = calculateRelationsCount(relationGeography);
      this.distributionText = this.translateService.instant('GEOGRAPHIC_DISTRIBUTION_BY_COUNTRY', {
        patients_counts: relationCount,
        percentage: Math.round((relationCount / this.totalPatientCount) * 1000) / Math.pow(10, 1),
        selected_country: this.form.value.selectedCountry,
      }) as string;
    }
  }

  private handleRelationGeographyFetched(relationGeography: RelationGeography): void {
    this.noRegionalData = relationGeography.length == 0 && this.form.value.selectedCountry != this.defaultValue;
    if (relationGeography.length > 0) {
      this.setPreferencesText(relationGeography);
      this.relationGeography$$.next(relationGeography);
    } else {
      this.relationGeography$$.next([]);
    }
  }

  private updateFormChangedState(): void {
    this.isFormChanged = this.form.dirty;
  }

  private loadRelationGeography(userId: string, filter_by?: PatientDistributionFilter, filter_value?: string): void {
    this.patientLinkService.loadRelationGeography(userId, filter_by, filter_value);
  }

  private setCountries(countryNames: string[]): void {
    this.countryOptions = [
      { label: 'All Countries', value: this.defaultValue },
      ...countryNames.map((countryName: string) => ({ label: countryName, value: countryName })),
    ];
  }

  private setDropdownValue(): void {
    const selectedValue = this.initialCountry || this.defaultValue;

    this.form.patchValue({ selectedCountry: selectedValue }, { emitEvent: false });
  }

  private loadDistributionData(): void {
    const selectedCountry = this.form.value.selectedCountry;
    if (selectedCountry === this.defaultValue) {
      this.loadRelationGeography(this.userId);
    } else {
      this.loadRelationGeography(this.userId, PatientDistributionFilter.Country, selectedCountry);
    }
  }

  public savePatientDistributionPreferences(): void {
    this.initialCountry = this.form.value.selectedCountry;
    let preferences = {};
    if (this.initialCountry != this.defaultValue) {
      preferences = {
        display_options: {
          patient_distribution: {
            distribution_type: PatientDistributionType.Geography,
            distribution_filter: PatientDistributionFilter.Country,
            value: this.initialCountry,
          },
        },
      };
    }

    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        successfulMessage: 'DISTRIBUTION_PREFERENCES_WERE_UPDATED',
      },
      () => {
        this.distributionPreferencesService.updateDistributionPreferences(preferences, this.userId);
      },
    );

    this.markFormUntouched();
  }

  public discardChanges(): void {
    this.setDropdownValue();
    this.markFormUntouched();
    this.loadDistributionData();
  }

  public markFormUntouched(): void {
    this.form.markAsUntouched();
    this.form.markAsPristine();
    this.updateFormChangedState();
  }
}
